import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react'

import { useUniqId } from '@surglogs-app/hooks'

interface CloseAllItemsDetail {
  id: string
}

export const useToggleHandlers = <T = HTMLButtonElement>({
  prefix,
  defaultValue,
}: {
  prefix: string
  defaultValue: boolean
}) => {
  const CLOSE_ALL_ITEMS_EVENT_NAME = `close-all-${prefix}-items`
  const [isExpanded, setExpanded] = useState(defaultValue)
  const id = useUniqId({ prefix })
  const ref = useRef<T>(null)

  const toggle = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      setExpanded((val) => !val)
      window.dispatchEvent(
        new CustomEvent(CLOSE_ALL_ITEMS_EVENT_NAME, { detail: { id } }),
      )
    },
    [CLOSE_ALL_ITEMS_EVENT_NAME, id],
  )

  const close = useCallback(() => {
    setExpanded(false)
  }, [])

  useEffect(() => {
    const handleCloseAll = (event: CustomEvent<CloseAllItemsDetail>) => {
      if (isExpanded && event.detail.id !== id) {
        close()
      }
    }

    window.addEventListener(
      CLOSE_ALL_ITEMS_EVENT_NAME,
      handleCloseAll as EventListener,
    )

    return () => {
      window.removeEventListener(
        CLOSE_ALL_ITEMS_EVENT_NAME,
        handleCloseAll as EventListener,
      )
    }
  }, [isExpanded, close, id, CLOSE_ALL_ITEMS_EVENT_NAME])

  return { id, ref, isExpanded, toggle, close }
}
